import type { EnvironmentProviders, Provider } from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { definePreset } from '@primeng/themes';
import Aura from '@primeng/themes/aura';
import { providePrimeNG, type PrimeNGConfigType } from 'primeng/config';
import { providePhoenixPlugin, type ProvidePhoenixPluginOptions } from '../angular/provide-phoenix-plugin';

/** Aura preset with blue primary scale (matches angular-plugin-test sample). */
export const phoenixPluginAuraPreset = definePreset(Aura, {
  semantic: {
    primary: {
      50: '{blue.50}',
      100: '{blue.100}',
      200: '{blue.200}',
      300: '{blue.300}',
      400: '{blue.400}',
      500: '{blue.500}',
      600: '{blue.600}',
      700: '{blue.700}',
      800: '{blue.800}',
      900: '{blue.900}',
      950: '{blue.950}',
    },
  },
});

/** Default `providePrimeNG` options for Phoenix plugins. */
export const defaultPhoenixPrimeNGConfig: PrimeNGConfigType = {
  ripple: true,
  theme: {
    options: {
      prefix: 'p',
      darkModeSelector: '.dark',
    },
    preset: phoenixPluginAuraPreset,
  },
};

export type ProvidePhoenixPluginWithPrimeNGOptions = ProvidePhoenixPluginOptions & {
  /** Full `providePrimeNG` config; defaults to {@link defaultPhoenixPrimeNGConfig}. */
  primeNG?: PrimeNGConfigType;
};

/**
 * Phoenix web-component plugin providers plus PrimeNG (Aura) and browser animations,
 * matching the angular-plugin-test `app.config` setup.
 */
export function providePhoenixPluginWithPrimeNG(
  options?: ProvidePhoenixPluginWithPrimeNGOptions,
): (Provider | EnvironmentProviders)[] {
  const { primeNG, ...phoenixOptions } = options ?? {};
  return [
    provideAnimations(),
    providePrimeNG(primeNG ?? defaultPhoenixPrimeNGConfig),
    ...providePhoenixPlugin(phoenixOptions),
  ];
}
